<script lang="ts">
import { computed, defineComponent, provide, ref, unref } from 'vue';

import {
  makeDirectionProps,
  makeDisabledProps,
  makeInputProps,
  makeReadonlyProps,
  makeThemeProps,
  makeValidationProps,
  provideTheme,
  useDirection,
  useFilteredInputProps,
  useInput,
  useProxyModel
} from '../../composable';
import { CdsInput, CdsInputLabel, CdsInputDescription } from '../CdsInput';

import type { Ref } from 'vue';

export interface ICheckboxGroupProvide {
  model: Ref<number[] | string[] | null | undefined>;
  isGroup: Ref<boolean>;
  disabledGroup: Ref<boolean>;
  readonlyGroup: Ref<boolean>;
  errorGroup: Ref<boolean>;
  handleInputCheckbox: (payload: THandleInputPayload) => void;
}
export type THandleInputPayload = {
  id: string;
  checked: boolean;
  value: string;
};

export const checkboxGroupProvideDefaultValue: ICheckboxGroupProvide = {
  model: ref(null),
  isGroup: ref(false),
  disabledGroup: ref(false),
  readonlyGroup: ref(false),
  errorGroup: ref(false),
  handleInputCheckbox: () => {}
};

export const CheckboxGroupKey = Symbol.for('cds:checkbox');
export const componentName = 'CdsCheckboxGroup';
export default defineComponent({
  name: componentName,
  components: { CdsInput, CdsInputLabel, CdsInputDescription },
  props: {
    ...makeInputProps(),
    ...makeValidationProps(),
    ...makeDirectionProps({
      direction: 'vertical' as const
    }),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeThemeProps()
  },
  emits: [
    /**
     * Событие при изменении значения (v-model)
     * @type { string[] }
     */
    'update:modelValue'
  ],
  setup(props, { slots }) {
    const model = useProxyModel(props, 'modelValue', () => []);
    const { themeClasses } = provideTheme(props);
    const { hasLabel, hasDescription } = useInput(props, slots, 'checkbox-group');
    const { directionClasses } = useDirection(props, componentName);
    const { inputProps } = useFilteredInputProps(props, ['direction']);

    const disabledGroup = computed(() => props.disabled);
    const readonlyGroup = computed(() => props.readonly);
    const errorGroup = computed(() => props.error);

    const handleInputCheckbox = ({ id, checked, value }: THandleInputPayload) => {
      const internalValue = value || id;

      if (!Array.isArray(unref(model))) return;

      if (checked) {
        model.value = [...unref(model), internalValue];
      } else {
        model.value = unref(model).filter((itemValue: string) => internalValue !== itemValue);
      }
    };

    provide(CheckboxGroupKey, {
      model,
      isGroup: ref(true),
      disabledGroup,
      readonlyGroup,
      errorGroup,
      handleInputCheckbox
    });

    return {
      inputProps,
      hasLabel,
      hasDescription,
      directionClasses,
      themeClasses
    };
  }
});
</script>

<template>
  <cds-input :id="id" class="cds-checkbox-group" :class="[directionClasses, themeClasses]" v-bind="{ ...inputProps }">
    <cds-input-label v-if="hasLabel" :text="label" tag="div">
      <!--
        @slot Лейбл
        @type string | html
      -->
      <slot name="label" />
    </cds-input-label>
    <cds-input-description v-if="hasDescription" :text="description">
      <!--
        @slot Описание
        @type string | html
      -->
      <slot name="description" />
    </cds-input-description>
    <div class="cds-checkbox-group__controls">
      <!--
        @slot Слот для чекбоксов
        @type CdsCheckbox
      -->
      <slot />
    </div>
    <template #messages>
      <!--
        @slot Сообщения и подсказки
        @type string | html
      -->
      <slot name="messages" />
    </template>
  </cds-input>
</template>
